import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useState } from "react";
import { AlertTriangle, Ban, CheckCircle2, CircleDot, Clock, Hammer, XCircle, type LucideIcon } from "lucide-react";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { STATUS_META, STATUS_TERMINAIS } from "@/components/prosthesis/prosthesis-meta";
import { usePendenciaDeMaterial } from "./use-pendencia-material";


const STATUS_ICON: Record<ProsthesisStatus, LucideIcon> = {
  AGUARDANDO_CONFIRMACAO: Clock,
  RECUSADO: Ban,
  ACEITO: CircleDot,
  EM_PRODUCAO: Hammer,
  CONCLUIDO: CheckCircle2,
  CANCELADO: XCircle,
};

/** Badge do status real — sem ação nenhuma. `compact` troca o texto por só o ícone, do mesmo
 *  tamanho (h-5) dos demais selects compactos da Planilha (Modelo, Estágio). Sem `compact`, vira
 *  um pill de texto h-7 igual aos selects de Estágio/Modelo do dialog de detalhe. */
export function ProductionStatusBadge({ status, compact }: { status: ProsthesisStatus; compact?: boolean }) {
  const meta = STATUS_META[status];
  const StatusIcon = STATUS_ICON[status];

  if (compact) {
    return (
      <span
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: meta.bg, color: meta.color }}
        title={meta.label}
      >
        <StatusIcon className="h-3 w-3" />
      </span>
    );
  }

  return (
    <span
      className="inline-flex h-7 max-w-full flex-1 items-center justify-center truncate rounded-full px-2 text-[11px] font-bold uppercase"
      style={{ backgroundColor: meta.bg, color: meta.color }}
      title={meta.label}
    >
      {meta.label}
    </span>
  );
}

/** Único gatilho "Finalizar" — a escolha entre Concluído/Cancelado acontece dentro do dialog de
 *  confirmação, nunca direto na linha (evita mis-click entre dois ícones colados). Não renderiza
 *  nada em status terminal (`CONCLUIDO`/`CANCELADO`/`RECUSADO`), já que não há mais o que finalizar.
 *  `variant="icon"` (default) é o ícone compacto da Planilha; `variant="button"` é o botão com
 *  texto usado no rodapé do dialog de detalhe. */
export function ProductionFinalizeButton({
  status,
  disabled,
  variant = "icon",
  serviceId,
  onConclude,
  onCancel,
}: {
  status: ProsthesisStatus;
  disabled?: boolean;
  variant?: "icon" | "button";
  /** Com o id, o diálogo avisa sobre material pendente antes de concluir. Sem ele, nada muda. */
  serviceId?: string;
  onConclude: () => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { motivo } = usePendenciaDeMaterial(serviceId);
  if (STATUS_TERMINAIS.includes(status)) return null;

  return (
    <>
      {variant === "button" ? (
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Finalizar Serviço
        </button>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          title="Finalizar serviço"
          aria-label="Finalizar serviço"
          className="text-zinc-400 transition-colors hover:text-brand-700 disabled:opacity-40 dark:text-muted-foreground dark:hover:text-primary"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm dark:bg-card" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_PRODUCAO.finalizarServico}>Finalizar serviço</DialogTitle>
            <DialogDescription>Escolha como esse serviço deve ser encerrado. A ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          {/* Avisa e deixa passar, de propósito: dado administrativo não trava quem está na
              bancada. A pendência continua visível na aba Materiais e no resumo da produção. */}
          {motivo && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong className="font-semibold">{motivo}.</strong> Dá para concluir assim — o custo
                deste trabalho fica incompleto até alguém registrar o material.
              </span>
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onConclude();
              }}
              className="flex flex-col items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-4 text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-400"
            >
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-semibold">Concluído</span>
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onCancel();
              }}
              className="flex flex-col items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-4 text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400"
            >
              <XCircle className="h-5 w-5" />
              <span className="text-sm font-semibold">Cancelado</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
